import React, { useEffect, useRef, useState } from "react";
import "./style.css";
import { CSSTransition } from "react-transition-group";

import {
  AVAILABLE_TYPES_PER_GENERATOR,
  AVAILABLE_THEMES_PER_TYPE,
  GRID_TYPE,
  SEASON,
  TIME,
  GENERATOR,
  LAYOUT_OPTIONS,
  FEATURE_KEYS,
  PATREON_TIERS,
  THEMES_LABEL,
  TYPE,
  AVAILABLE_WILDERNESS_TYPES,
  WILDERNESS_TYPE_LABEL,
  WILDERNESS_TYPE,
  AVAILABLE_OVERLAYS,
  OVERLAY_LABEL,
  OVERLAY,
} from "../../constants";

import Switch from "../../components/Switch";

import PaperBottom from "../../svgs/PaperBottom";
import PaperTop from "../../svgs/PaperTop";
import GeneratorButton from "../../components/GeneratorButton";
import Divider from "../../svgs/Divider";
import Dropdown from "../../components/Dropdown";
import SquareButton from "../../components/SquareButton";
import {
  getFeatureSetting,
  getMapSizeMax,
  isAllThemeOptionsDisabled,
  isWildernessTypeEnabled,
} from "../../utils";
import SectionLabel from "../../components/SectionLabel";
import AdvancedSettings from "./AdvancedSettings";
import MapSize from "./MapSize";
import LShape from "../../svgs/LShape";
import VisualsDropdown from "../../components/VisualsDropdown";

const LeftPanel = ({
  generateMap,
  loading,
  isAuthenticated,
  handleLogin,
  userTier,
  setShowHelper,
  user,
  handleLogout,
}) => {
  const defaultMapSize = Math.floor(Math.random() * (3 - 2 + 1)) + 2;

  const queryParams = new URLSearchParams(window.location.search);
  const queryParamsType = queryParams.get("type");

  const initialState = {
    generator: GENERATOR.DUNGEON,
    name: "",
    visuals: {
      type: TYPE.DUNGEON,
      theme: AVAILABLE_THEMES_PER_TYPE[TYPE.DUNGEON][0],
      variation: null,
    },
    layout: LAYOUT_OPTIONS[0],
    gridType: GRID_TYPE.THIN,
    wildernessType: WILDERNESS_TYPE.FOREST,
    overlay: OVERLAY.NONE,
    time: null,
    season: null,
    roadEvent: false,
    includeTavern: false,
    mapSize: defaultMapSize,
    quality: 70,
    dmGuide: false,
    basement: false,
    basementGuide: true,
    branded: true,
    advancedSettings: {
      show: false,
      partyLevel: 5,
      partySize: 4,
      difficulty: "medium",
      seed: {
        show: false,
        value: null,
      },
      stairs: "random",
    },
  };
  const [panelState, setPanelState] = useState(initialState);
  const [types, setTypes] = useState(
    AVAILABLE_TYPES_PER_GENERATOR[panelState.generator]
  );
  const [typeThemes, setTypeThemes] = useState([]);

  const paneContainerRef = useRef(null);
  const visualsRef = useRef(null);
  const visualDropdownRef = useRef(null);
  const layoutRef = useRef(null);
  const timeRef = useRef(null);
  const seasonRef = useRef(null);
  const wildernessTypeRef = useRef(null);
  const overlayRef = useRef(null);
  const mapSizeRef = useRef(null);
  const roadEventRef = useRef(null);
  const includeTavernRef = useRef(null);
  const advancedSettingsSwitchRef = useRef(null);
  const dmGuideSwitchRef = useRef(null);
  const basementSwitchRef = useRef(null);
  const basementGuideSwitchRef = useRef(null);
  const advancedSettingsRef = useRef(null);

  const showLayout = panelState.generator === GENERATOR.DUNGEON;
  const showTime = panelState.generator === GENERATOR.TAVERN;
  const showSeason = panelState.generator === GENERATOR.TAVERN;
  const showWildernessType = panelState.generator === GENERATOR.WILDERNESS;
  const showOverlay = panelState.generator === GENERATOR.WILDERNESS;
  const showMapSize =
    panelState.generator == GENERATOR.DUNGEON ||
    panelState.generator == GENERATOR.ROAD ||
    panelState.generator == GENERATOR.WILDERNESS;
  const showRoadEvent = panelState.generator === GENERATOR.ROAD;
  const showIncludeTavern = panelState.generator === GENERATOR.ROAD;
  const showDmGuide =
    panelState.generator === GENERATOR.DUNGEON ||
    panelState.generator === GENERATOR.TAVERN;
  const showBasement = panelState.generator === GENERATOR.TAVERN;
  // The basement guide page is a DM Guide page, so it only makes sense when
  // there is both a basement to describe and a guide to put it in.
  const showBasementGuide =
    panelState.generator === GENERATOR.TAVERN &&
    panelState.basement &&
    panelState.dmGuide;
  const showAdvancedSettings = true;

  const allThemeOptionsDisabled = isAllThemeOptionsDisabled(
    AVAILABLE_THEMES_PER_TYPE[panelState.visuals.type],
    userTier
  );

  const generateMapButtonDisabled = allThemeOptionsDisabled || loading;
  const layoutDisabled = !getFeatureSetting(
    FEATURE_KEYS.LAYOUT_OPTIONS,
    userTier
  );
  const mapSizeDisabled = !getFeatureSetting(FEATURE_KEYS.MAP_SIZE, userTier);
  const dmGuideDisabled = !getFeatureSetting(FEATURE_KEYS.DM_GUIDE, userTier);
  const basementDisabled = !getFeatureSetting(
    FEATURE_KEYS[GENERATOR.TAVERN].BASEMENT,
    userTier
  );
  const basementGuideDisabled = !getFeatureSetting(
    FEATURE_KEYS[GENERATOR.TAVERN].BASEMENT_GUIDE,
    userTier
  );
  const timeOfDayDisabled = !getFeatureSetting(
    FEATURE_KEYS[panelState.generator].TIME_OF_DAY,
    userTier
  );
  const seasonDisabled = !getFeatureSetting(
    FEATURE_KEYS[panelState.generator].SEASON,
    userTier
  );
  const wildernessTypeSetting = getFeatureSetting(
    FEATURE_KEYS[GENERATOR.WILDERNESS].TYPE,
    userTier
  );
  const wildernessTypeGated = AVAILABLE_WILDERNESS_TYPES.some(
    (t) => !isWildernessTypeEnabled(t, wildernessTypeSetting)
  );
  const overlayDisabled = !getFeatureSetting(
    FEATURE_KEYS[GENERATOR.WILDERNESS].OVERLAY,
    userTier
  );
  const roadEventDisabled = !getFeatureSetting(
    FEATURE_KEYS[panelState.generator].EVENT,
    userTier
  );
  const includeTavernDisabled = !getFeatureSetting(
    FEATURE_KEYS[panelState.generator].INCLUDE_TAVERN,
    userTier
  );
  const advancedSettingsDisabled = !getFeatureSetting(
    FEATURE_KEYS.ADVANCED_SETTINGS,
    userTier
  );

  const mapSizeMin = 2;
  const mapSizeMax = getMapSizeMax(panelState);

  // When generator changes, reset state
  useEffect(() => {
    setTypes(AVAILABLE_TYPES_PER_GENERATOR[panelState.generator]);
    setTypeThemes([]);
    setPanelState({
      ...initialState,
      branded: panelState.branded,
      generator: panelState.generator,
      mapSize:
        panelState.generator === GENERATOR.DUNGEON
          ? defaultMapSize
          : mapSizeMin,
    });
  }, [panelState.generator]);

  useEffect(() => {
    if (
      queryParamsType &&
      AVAILABLE_TYPES_PER_GENERATOR[GENERATOR.DUNGEON].includes(queryParamsType)
    ) {
      setPanelState({
        ...panelState,
        visuals: {
          ...panelState.visuals,
          type: queryParamsType,
        },
      });
    }
  }, [queryParamsType]);

  // When type changes, update themes available
  useEffect(() => {
    if (panelState.visuals.type) {
      setTypeThemes(AVAILABLE_THEMES_PER_TYPE[panelState.visuals.type]);

      setShowHelper(!allThemeOptionsDisabled);

      // Set the first theme in the type list if it's not disabled
      if (!allThemeOptionsDisabled) {
        setPanelState({
          ...panelState,
          visuals: {
            ...panelState.visuals,
            theme: AVAILABLE_THEMES_PER_TYPE[panelState.visuals.type][0],
          },
        });
      }
    }
    if (panelState.mapSize > mapSizeMax) {
      setPanelState({ ...panelState, mapSize: mapSizeMax });
    }
  }, [panelState.visuals.type]);

  useEffect(() => {
    if (panelState.mapSize > getMapSizeMax(panelState)) {
      setPanelState({ ...panelState, mapSize: getMapSizeMax(panelState) });
    }
  }, [panelState.visuals.theme]);

  // When the user is authenticated, remove the branded option
  useEffect(() => {
    if (isAuthenticated && userTier !== PATREON_TIERS.FREE) {
      setPanelState({
        ...panelState,
        branded: false,
      });
    }
  }, [isAuthenticated, userTier]);

  const scrollToBottom = () => {
    if (paneContainerRef.current) {
      paneContainerRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  };

  const handleGeneratorChange = (type) => {
    setPanelState({ ...panelState, generator: type });
  };

  const handleNameChange = (text) => {
    setPanelState({ ...panelState, name: text });
  };

  const handleTypeSelect = (type) => {
    setPanelState({
      ...panelState,
      visuals: { ...panelState.visuals, type },
    });
  };

  const handleThemeSelect = (theme) => {
    setPanelState({
      ...panelState,
      visuals: { ...panelState.visuals, theme },
    });
  };

  const handleGridTypeChange = (type) => {
    setPanelState({ ...panelState, gridType: type });
  };

  const handleWildernessTypeChange = (type) => {
    setPanelState({ ...panelState, wildernessType: type });
  };

  const handleOverlayChange = (overlay) => {
    setPanelState({ ...panelState, overlay });
  };

  const handleLayoutChange = (layout) => {
    setPanelState({ ...panelState, layout });
  };

  const handleTimeChange = (time) => {
    setPanelState({ ...panelState, time: time });
  };

  const handleSeasonChange = (season) => {
    setPanelState({ ...panelState, season: season });
  };

  const handleRoadEventChange = (value) => {
    setPanelState({ ...panelState, roadEvent: value });
  };

  const handleIncludeTavernChange = (value) => {
    setPanelState({ ...panelState, includeTavern: value });
  };

  const handleDmGuideChange = (value) => {
    setPanelState({ ...panelState, dmGuide: value });
  };

  const handleBasementChange = (value) => {
    // Turning the basement on re-arms its guide page: the sub-switch defaults
    // on each time the basement is enabled.
    setPanelState({
      ...panelState,
      basement: value,
      basementGuide: value ? true : panelState.basementGuide,
    });
  };

  const handleBasementGuideChange = (value) => {
    setPanelState({ ...panelState, basementGuide: value });
  };

  // const handleQualityChange = (value) => {
  //   setPanelState({ ...panelState, quality: Number(value) });
  // };

  const handleMapSizeChange = (value) => {
    setPanelState({ ...panelState, mapSize: value });
  };

  const handleMapSizeBlur = () => {
    setPanelState({
      ...panelState,
      mapSize: Math.max(mapSizeMin, Math.min(mapSizeMax, panelState.mapSize)),
    });
  };

  const handleSeedShow = (show) => {
    scrollToBottom();
    setPanelState({
      ...panelState,
      advancedSettings: {
        ...panelState.advancedSettings,
        seed: {
          ...initialState.advancedSettings.seed,
          show,
          value: show ? initialState.advancedSettings.seed.value : null,
        },
      },
    });
  };

  const handleSeedChange = (value) => {
    setPanelState({
      ...panelState,
      advancedSettings: {
        ...panelState.advancedSettings,
        seed: { ...panelState.advancedSettings.seed, value },
      },
    });
  };

  const handleAdvancedSettingsShowChange = (show) => {
    scrollToBottom();
    setPanelState({
      ...panelState,
      advancedSettings: {
        ...panelState.advancedSettings,
        show,
      },
    });
  };

  const handleStairsChange = (value) => {
    setPanelState({
      ...panelState,
      advancedSettings: { ...panelState.advancedSettings, stairs: value },
    });
  };

  const handlePartyLevelChange = (value) => {
    setPanelState({
      ...panelState,
      advancedSettings: { ...panelState.advancedSettings, partyLevel: value },
    });
  };

  const handlePartyLevelBlur = () => {
    setPanelState({
      ...panelState,
      advancedSettings: {
        ...panelState.advancedSettings,
        partyLevel: Math.max(
          1,
          Math.min(20, panelState.advancedSettings.partyLevel)
        ),
      },
    });
  };

  const handlePartySizeChange = (value) => {
    setPanelState({
      ...panelState,
      advancedSettings: { ...panelState.advancedSettings, partySize: value },
    });
  };

  const handleDifficultyChange = (value) => {
    setPanelState({
      ...panelState,
      advancedSettings: { ...panelState.advancedSettings, difficulty: value },
    });
  };

  return (
    <div className="side-panel">
      <div className="header">
        <a
          href="https://arcanecollector.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img src="images/ph_infinity-bold.svg" alt="Infinity" />
        </a>
        <div className="titleContainer">
          <span className="title">Mythical Maps</span>
          <span className="subTitle">INFINITY</span>
        </div>
      </div>
      <div className="upperSection">
        <div className="generatorSection">
          <GeneratorButton
            text={GENERATOR.DUNGEON}
            handleClick={handleGeneratorChange}
            active={panelState.generator === GENERATOR.DUNGEON}
          />
          <GeneratorButton
            text={GENERATOR.TAVERN}
            handleClick={handleGeneratorChange}
            active={panelState.generator === GENERATOR.TAVERN}
          />
          <GeneratorButton
            text={GENERATOR.WILDERNESS}
            handleClick={handleGeneratorChange}
            active={panelState.generator === GENERATOR.WILDERNESS}
          />
          <GeneratorButton
            text={GENERATOR.ROAD}
            handleClick={handleGeneratorChange}
            active={panelState.generator === GENERATOR.ROAD}
          />
        </div>
        <PaperBottom />
      </div>
      <div className="mainContainer" ref={paneContainerRef}>
        {!isAuthenticated ? (
          <div
            style={{ width: "90%", marginTop: "1em", marginBottom: ".5em" }}
            onClick={handleLogin}
          >
            <div className="unlockButton">Unlock Features via Patreon!</div>
          </div>
        ) : (
          <div className={`loginStatus`}>
            <div>
              Patreon Tier: {user?.tier || "Collector"} (
              <a
                href="javascript:void(0)"
                onClick={handleLogout}
                style={{ zIndex: 1 }}
              >
                Logout
              </a>
              )
            </div>
          </div>
        )}
        <div className="sectionContainer" style={{ marginTop: 10 }}>
          <div className="nameContainer">
            <label className="label">Name</label>
            <input
              type="text"
              className="textField"
              placeholder="e.g. The Boar's Head"
              value={panelState.name}
              onChange={(e) => handleNameChange(e.target.value)}
            />
          </div>
        </div>
        <CSSTransition
          in={types.length > 0}
          nodeRef={visualsRef}
          timeout={200}
          classNames="fade"
          unmountOnExit
        >
          <div
            className="sectionContainer"
            style={{ marginTop: "20px" }}
            ref={visualsRef}
          >
            <div className="visualsContainer">
              <label className="label">Visuals</label>
              <div className="dropdownSelectionContainer">
                <label className="smallLabel">Type</label>
                <div className="typeButtonContainer">
                  {types.map((type, i) => (
                    <div
                      className={`${
                        panelState.visuals.type === type
                          ? "typeButtonSelected"
                          : "typeButton"
                      } selectNone`}
                      onClick={() => handleTypeSelect(type)}
                      key={i}
                    >
                      {type}
                    </div>
                  ))}
                </div>
              </div>
              <CSSTransition
                in={typeThemes.length > 0}
                nodeRef={visualDropdownRef}
                timeout={200}
                classNames="fade"
                unmountOnExit
              >
                <div
                  className="dropdownSelectionContainer themeDropdownContainer"
                  style={{ marginLeft: "20px" }}
                  ref={visualDropdownRef}
                >
                  {allThemeOptionsDisabled && (
                    <div
                      className="smallLabel"
                      style={{ color: "#ED766F", fontSize: "11px" }}
                    >
                      Select Type with available Themes or unlock with Patreon
                    </div>
                  )}
                  <div>
                    <LShape />
                    <label className="smallLabel">
                      {panelState.visuals.type} Theme
                    </label>
                  </div>
                  <VisualsDropdown
                    options={typeThemes}
                    value={panelState.visuals.theme}
                    onChange={handleThemeSelect}
                    optionLabels={typeThemes.map((t) => THEMES_LABEL[t])}
                    userTier={userTier}
                  />
                </div>
              </CSSTransition>
              {/* <div
                className="dropdownSelectionContainer"
                style={{ marginLeft: "20px" }}
              >
                <div style={{ marginLeft: "20px" }}>
                  <LShape />
                  <label className="smallLabel">{"Temple"} Variations</label>
                </div>
                <Dropdown />
              </div> */}
              <Divider />
            </div>
          </div>
        </CSSTransition>
        <CSSTransition
          in={showWildernessType}
          nodeRef={wildernessTypeRef}
          timeout={200}
          classNames="fade"
          unmountOnExit
        >
          <div className="sectionContainer" ref={wildernessTypeRef}>
            <SectionLabel
              text="Type"
              disabled={wildernessTypeGated}
              featureKey={FEATURE_KEYS[GENERATOR.WILDERNESS].TYPE}
            />
            <div className="typeButtonContainer">
              {AVAILABLE_WILDERNESS_TYPES.map((type, i) => {
                const enabled = isWildernessTypeEnabled(
                  type,
                  wildernessTypeSetting
                );
                const selected = panelState.wildernessType === type;
                return (
                  <div
                    key={i}
                    className={`${
                      !enabled
                        ? "typeButtonDisabled"
                        : selected
                        ? "typeButtonSelected"
                        : "typeButton"
                    } selectNone`}
                    onClick={() => enabled && handleWildernessTypeChange(type)}
                  >
                    {WILDERNESS_TYPE_LABEL[type]}
                  </div>
                );
              })}
            </div>
          </div>
        </CSSTransition>
        <CSSTransition
          in={showOverlay}
          nodeRef={overlayRef}
          timeout={200}
          classNames="fade"
          unmountOnExit
        >
          <div
            className={`sectionContainer ${
              overlayDisabled && "disabledSection"
            }`}
            ref={overlayRef}
          >
            <SectionLabel
              text="Weather Options"
              disabled={overlayDisabled}
              featureKey={FEATURE_KEYS[GENERATOR.WILDERNESS].OVERLAY}
            />
            <Dropdown
              options={AVAILABLE_OVERLAYS}
              optionLabels={AVAILABLE_OVERLAYS.map((o) => OVERLAY_LABEL[o])}
              value={panelState.overlay}
              onChange={handleOverlayChange}
              disabled={overlayDisabled}
              featureKey={FEATURE_KEYS[GENERATOR.WILDERNESS].OVERLAY}
            />
          </div>
        </CSSTransition>
        <div className="sectionContainer">
          <label className="label">Grid Type</label>
          <div className="gridTypeButtonContainer">
            <SquareButton
              text={GRID_TYPE.NONE}
              handleClick={handleGridTypeChange}
              active={panelState.gridType === GRID_TYPE.NONE}
            />
            <SquareButton
              text={GRID_TYPE.THIN}
              handleClick={handleGridTypeChange}
              active={panelState.gridType === GRID_TYPE.THIN}
            />
            <SquareButton
              text={GRID_TYPE.THICK}
              handleClick={handleGridTypeChange}
              active={panelState.gridType === GRID_TYPE.THICK}
            />
            <SquareButton
              text={GRID_TYPE.DOTTED}
              handleClick={handleGridTypeChange}
              active={panelState.gridType === GRID_TYPE.DOTTED}
            />
            <SquareButton
              text={GRID_TYPE.HEX}
              handleClick={handleGridTypeChange}
              active={panelState.gridType === GRID_TYPE.HEX}
            />
          </div>
        </div>
        <CSSTransition
          in={showMapSize}
          nodeRef={mapSizeRef}
          timeout={200}
          classNames="fade"
          unmountOnExit
        >
          <MapSize
            mapSizeDisabled={mapSizeDisabled}
            mapSizeMin={mapSizeMin}
            mapSizeMax={mapSizeMax}
            panelState={panelState}
            setPanelState={setPanelState}
            handleMapSizeChange={handleMapSizeChange}
            handleMapSizeBlur={handleMapSizeBlur}
            mapSizeRef={mapSizeRef}
          />
        </CSSTransition>
        <CSSTransition
          in={showLayout}
          nodeRef={layoutRef}
          timeout={200}
          classNames="fade"
          unmountOnExit
        >
          <div
            className={`sectionContainer ${
              layoutDisabled && "disabledSection"
            }`}
            ref={layoutRef}
          >
            <SectionLabel
              text="Layout Options"
              disabled={layoutDisabled}
              featureKey={FEATURE_KEYS.LAYOUT_OPTIONS}
            />
            <Dropdown
              options={LAYOUT_OPTIONS}
              optionLabels={LAYOUT_OPTIONS.map((opt) =>
                opt
                  .split("_")
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(" ")
              )}
              value={panelState.layout}
              onChange={handleLayoutChange}
              disabled={layoutDisabled}
              featureKey={FEATURE_KEYS.LAYOUT_OPTIONS}
            />
          </div>
        </CSSTransition>

        <CSSTransition
          in={showTime}
          nodeRef={timeRef}
          timeout={200}
          classNames="fade"
          unmountOnExit
        >
          <div
            className={`sectionContainer ${
              timeOfDayDisabled && "disabledSection"
            }`}
            ref={timeRef}
          >
            <SectionLabel
              text="Time"
              disabled={timeOfDayDisabled}
              featureKey={FEATURE_KEYS[panelState.generator].TIME_OF_DAY}
            />
            <div className="gridTypeButtonContainer">
              <SquareButton
                text={TIME.DAY}
                handleClick={handleTimeChange}
                active={panelState.time === TIME.DAY}
                disabled={timeOfDayDisabled}
                defeaultSelection
              />
              <SquareButton
                text={TIME.NIGHT}
                handleClick={handleTimeChange}
                active={panelState.time === TIME.NIGHT}
                disabled={timeOfDayDisabled}
              />
            </div>
          </div>
        </CSSTransition>
        <CSSTransition
          in={showSeason}
          nodeRef={seasonRef}
          timeout={200}
          classNames="fade"
          unmountOnExit
        >
          <div
            className={`sectionContainer ${
              seasonDisabled && "disabledSection"
            }`}
            ref={seasonRef}
          >
            <SectionLabel
              text="Season"
              disabled={seasonDisabled}
              featureKey={FEATURE_KEYS[panelState.generator].SEASON}
            />
            <div className="gridTypeButtonContainer">
              <SquareButton
                text={SEASON.SPRING}
                handleClick={handleSeasonChange}
                active={panelState.season === SEASON.SPRING}
                disabled={seasonDisabled}
                defeaultSelection
              />
              <SquareButton
                text={SEASON.FALL}
                handleClick={handleSeasonChange}
                active={panelState.season === SEASON.FALL}
                disabled={seasonDisabled}
              />
              <SquareButton
                text={SEASON.WINTER}
                handleClick={handleSeasonChange}
                active={panelState.season === SEASON.WINTER}
                disabled={seasonDisabled}
              />
            </div>
          </div>
        </CSSTransition>
        <CSSTransition
          in={showRoadEvent}
          nodeRef={roadEventRef}
          timeout={200}
          classNames="fade"
          unmountOnExit
        >
          <div
            className={`sectionContainer ${
              roadEventDisabled && "disabledSection"
            }`}
            ref={roadEventRef}
          >
            <SectionLabel
              text="Road Event"
              disabled={roadEventDisabled}
              featureKey={FEATURE_KEYS[panelState.generator].EVENT}
            />
            <div style={{ marginTop: 5 }}>
              <Switch
                checked={panelState.roadEvent}
                onChange={handleRoadEventChange}
                disabled={roadEventDisabled}
              />
            </div>
          </div>
        </CSSTransition>
        <CSSTransition
          in={showIncludeTavern}
          nodeRef={includeTavernRef}
          timeout={200}
          classNames="fade"
          unmountOnExit
        >
          <div
            className={`sectionContainer ${
              includeTavernDisabled && "disabledSection"
            }`}
            ref={includeTavernRef}
          >
            <SectionLabel
              text="Include Tavern"
              disabled={includeTavernDisabled}
              featureKey={FEATURE_KEYS[panelState.generator].INCLUDE_TAVERN}
            />
            <div style={{ marginTop: 5 }}>
              <Switch
                checked={panelState.includeTavern}
                onChange={handleIncludeTavernChange}
                disabled={includeTavernDisabled}
              />
            </div>
          </div>
        </CSSTransition>
        {/* <div className="sectionContainer">
          <label className="label">Quality (DPI)</label>
          <VisualsDropdown
            options={[70, 150, 300]}
            onChange={handleQualityChange}
          />
        </div> */}
        <CSSTransition
          in={showDmGuide}
          nodeRef={dmGuideSwitchRef}
          timeout={200}
          classNames="fade"
          unmountOnExit
        >
          <div
            className={`sectionContainer ${
              dmGuideDisabled && "disabledSection"
            }`}
            ref={dmGuideSwitchRef}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <SectionLabel
                text="DM Guide"
                disabled={dmGuideDisabled}
                featureKey={FEATURE_KEYS.DM_GUIDE}
              />
              <label
                className="smallLabel"
                style={{ marginTop: 4, marginBottom: 4 }}
              >
                Generates a PDF file with descriptions
              </label>
              <Switch
                checked={panelState.dmGuide}
                onChange={handleDmGuideChange}
                disabled={dmGuideDisabled}
              />
            </div>
          </div>
        </CSSTransition>
        <CSSTransition
          in={showBasement}
          nodeRef={basementSwitchRef}
          timeout={200}
          classNames="fade"
          unmountOnExit
        >
          <div
            className={`sectionContainer ${
              basementDisabled && "disabledSection"
            }`}
            ref={basementSwitchRef}
          >
            <div style={{ display: "flex", flexDirection: "column" }}>
              <SectionLabel
                text="Basement"
                disabled={basementDisabled}
                featureKey={FEATURE_KEYS[GENERATOR.TAVERN].BASEMENT}
              />
              <label
                className="smallLabel"
                style={{ marginTop: 4, marginBottom: 4 }}
              >
                Generates a cellar map beneath the tavern
              </label>
              <Switch
                checked={panelState.basement}
                onChange={handleBasementChange}
                disabled={basementDisabled}
              />
            </div>
          </div>
        </CSSTransition>
        <CSSTransition
          in={showBasementGuide}
          nodeRef={basementGuideSwitchRef}
          timeout={200}
          classNames="fade"
          unmountOnExit
        >
          <div
            className={`sectionContainer ${
              basementGuideDisabled && "disabledSection"
            }`}
            ref={basementGuideSwitchRef}
          >
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div>
                <LShape />
                <SectionLabel
                  text="Basement DM Notes"
                  disabled={basementGuideDisabled}
                  featureKey={FEATURE_KEYS[GENERATOR.TAVERN].BASEMENT_GUIDE}
                />
              </div>
              <label
                className="smallLabel"
                style={{ marginTop: 4, marginBottom: 4 }}
              >
                Adds a basement page to the DM Guide PDF
              </label>
              <Switch
                checked={panelState.basementGuide}
                onChange={handleBasementGuideChange}
                disabled={basementGuideDisabled}
              />
            </div>
          </div>
        </CSSTransition>

        {/* Advanced Settings Switch */}
        <CSSTransition
          in={showAdvancedSettings}
          nodeRef={advancedSettingsSwitchRef}
          timeout={200}
          classNames="fade"
          unmountOnExit
        >
          <div className={`sectionContainer`} ref={advancedSettingsSwitchRef}>
            <label className="label">Advanced Settings</label>
            <div style={{ marginTop: 4 }}>
              <Switch
                checked={panelState.advancedSettings.show}
                onChange={handleAdvancedSettingsShowChange}
              />
            </div>
          </div>
        </CSSTransition>
        <CSSTransition
          in={panelState.advancedSettings.show}
          nodeRef={advancedSettingsRef}
          timeout={200}
          classNames="fade"
          unmountOnExit
        >
          <AdvancedSettings
            advancedSettingsRef={advancedSettingsRef}
            disabled={advancedSettingsDisabled}
            panelState={panelState}
            handleStairsChange={handleStairsChange}
            handlePartyLevelChange={handlePartyLevelChange}
            handlePartyLevelBlur={handlePartyLevelBlur}
            handlePartySizeChange={handlePartySizeChange}
            handleDifficultyChange={handleDifficultyChange}
            handleSeedShow={handleSeedShow}
            handleSeedChange={handleSeedChange}
          />
        </CSSTransition>
      </div>
      <div className="lowerSection">
        <PaperTop />
        <div className="bottomSection">
          <div className="advancedSectionContainer" style={{ width: "90%" }}>
            <div
              className="unlockButton selectNone"
              style={{
                opacity: generateMapButtonDisabled ? 0.5 : 1,
                pointerEvents: generateMapButtonDisabled ? "none" : "auto",
              }}
              onClick={
                generateMapButtonDisabled
                  ? undefined
                  : () => generateMap(panelState)
              }
            >
              Generate Map
            </div>
          </div>
        </div>
        <div className="divider" />
        <div
          className="footer"
          onClick={() =>
            window.open("https://www.patreon.com/ArcaneCollector", "_blank")
          }
        >
          <span className="footerLabel">Support us on </span>{" "}
          <img src="images/patreon-dark.png" alt="Patreon logo" />{" "}
          <img src="images/patreon-label.png" alt="Patreon label" />
        </div>
      </div>
    </div>
  );
};

export default LeftPanel;
